// ─── Five Workforce Operating Modes ──────────────────────────────────────────
export type WorkforceMode = 'Human' | 'Human+Machine' | 'RPA' | 'GenAI' | 'Agentic'

// ─── Headcount Reconciliation ─────────────────────────────────────────────────
export type HeadcountConfidence = 'High' | 'Medium' | 'Low'

export interface HeadcountReconciliationResult {
  extractedCount: number
  sourceRowCount: number
  confidence: HeadcountConfidence
}
export type AgentSubtype = 'utility' | 'orchestrator' | 'super'
export type SkillStatus = 'keep' | 'build' | 'retire'
export type AutomationCategory = 'Agentic' | 'RPA' | 'GenAI'

// ─── Dashboard Data Schema ────────────────────────────────────────────────────
export interface Task {
  number: string
  name: string
  skills: string
  time_pct: string
  hours: string
  cost: string
  value: string
  mode: WorkforceMode
  mode_raw?: string
  // % of this task's hours that AI realistically frees — evaluated task-by-task.
  // Mode-anchored bands: Human=0, Human+Machine 20–65, RPA 75–95, GenAI 55–88, Agentic 68–95.
  automation_share?: number
}

export interface Skill {
  name: string
  status: SkillStatus
}

export interface Role {
  ftes: number
  capacity_freed_pct?: number   // 20–45% range
  annual_value?: string
  current_tasks: Task[]
  skills?: Skill[]
}

// Movement pathway from a current role to a future archetype (TSCV Step 6)
export interface SourceRole {
  role: string           // exact current role name
  fit_pct: number        // 0–100: how well this role maps to the archetype
  fit_reason?: string    // 1-sentence explanation of why this role fits
  dev_actions: string[]  // 2–4 specific development actions needed
}

// Archetype = future-state role in the Workforce Flow Sankey
export interface Archetype {
  name: string
  ftes: number
  description?: string        // 1–2 sentences: what this role does post-transformation
  source_roles: SourceRole[]  // movement hypothesis: which current roles feed this archetype
  tasks: Task[]
  skills?: Skill[]            // keep/build/retire skill evolution for this archetype
}

export interface Subfunction {
  name: string
  function?: string             // parent business function (e.g., 'HR', 'Finance')
  roles: Record<string, Role>
  archetypes?: Record<string, Archetype>
  /** Combined GCC dashboards only: the branch this subfunction came from. Drives the Workforce Flow sections. */
  gccBranch?: GccWorkforceBranch
}

/** The two branches of a combined GCC dashboard: the GCC-eligible residual and the Retained organization. */
export type GccWorkforceBranch = 'gcc' | 'retained'

// Matches the template's native automationBuilds array format
export interface AutomationBuild {
  name: string
  type: string              // "Agentic AI" | "RPA Automation" | "GenAI Automation"
  category: 'agentic' | 'rpa' | 'genai'
  function?: string         // parent business function (e.g., 'HR', 'Finance')
  description: string
  subfunctions: string[]   // impacted subfunction names
  ftes: number             // FTE impact as a number
  timeline: string         // e.g. "Q2 2026"
  cost: string             // e.g. "$450K"
  roi: string              // e.g. "12 months"
  tasks: string[]          // task names automated
  impacted_roles?: string[] // role names affected — used for Tab 4 portfolio cross-link scoring
  effort?: 'low' | 'medium' | 'high'
  impact?: 'low' | 'medium' | 'high'
}

// ─── Process Redesign Roadmap (Tab 4) ────────────────────────────────────────
export interface ProcessAIActivation {
  name: string
  mode: WorkforceMode
  impactDesc?: string       // free-form impact text e.g. "Reduces review time from 3 days to 4 hours"
  horizon: 'quick-win' | 'strategic'
  // Extended fields — required for standalone activations[] backlog view
  desc?: string             // 2-sentence what it does + measurable impact
  timeline?: string         // e.g. "6–9 months"
  capacityImpact?: string   // e.g. "40% freed in Steps 1–3"
  effort?: 'low' | 'medium' | 'high'
  impact?: 'low' | 'medium' | 'high'
  roles?: string            // affected roles, comma-separated
  priority?: number         // 1 = highest
  status?: 'active' | 'deprioritized'
}

export interface ProcessStep {
  number: number
  name: string
  description: string
  currentDuration: string  // e.g. "3–5 days"
  currentFTEs: number
  roles: string[]          // role names
  aiActivation: ProcessAIActivation | null
  // Step-level flag: true = step remains primarily human-led post-AI.
  // Renders a "human" badge on the step track (matches JM Family layout).
  human: boolean
  // Code-computed, role-weighted average of capacity_freed_pct across `roles`.
  // Feeds the quantified narrative without trusting the LLM for step-level %.
  computedAutomationShare?: number
}

export interface ProcessKeyRole {
  roleName: string
  currentFocus: string
  futureShift: string
  artisan?: boolean         // true = irreplaceable human role; always capacity 0
  process_role?: string     // links role to specific steps e.g. "Leads Steps 3–5"
  ftes?: number             // AI-generated per-process estimate; informed by per-subfunction FTE context at generation time
  capacity?: number         // code-computed capacity freed % (0–100); never from LLM
}

export interface ProcessArea {
  id: string                // required, kebab-case slug e.g. "hire-to-retire"
  name: string
  description: string
  function: string
  steps: ProcessStep[]
  keyRoles: ProcessKeyRole[]
  activations?: ProcessAIActivation[]  // standalone backlog-ready activation items per area
  quickWins: string[]
  strategicInitiatives: string[]
  // Emoji/icon hint for the area card header (e.g. "📦", "🛠️").
  icon?: string
}

export interface DashboardData {
  subfunctions: Subfunction[]
  automation_builds?: AutomationBuild[]
  process_areas?: ProcessArea[]
  journey_narrative?: string[]
  closing_narrative?: string[]
}

// ─── Client Branding ──────────────────────────────────────────────────────────
export interface ClientBranding {
  clientName: string
  primaryColor: string       // hex e.g. "#0047BB"
  primaryColorDark: string   // auto-calculated darker shade
  primaryColorLight: string  // auto-calculated lighter shade
  accentColor?: string       // optional second brand color — literal accent on the v3 shell (draft pill only; header strip stays primary-colored)
  isWip: boolean             // shows WIP badge if true
  logoDataUrl?: string       // optional client logo as a base64 data URL (self-contained, no remote refs)
  versionLabel?: string      // free-text version pill shown in the header (e.g. "V1"); hidden when blank
}

export interface BrandColorLookupPayload {
  companyName: string
  provider: AIProvider
  apiKey: string
}

// High: official domain (brand guidelines/press kit/site), ideally corroborated by a second
// source. Medium: a single official-domain mention, or multiple independent third-party
// sources agreeing. Low: a single uncorroborated third-party/reference source, or a guess —
// treated the same as "nothing found" by callers (see BrandColorLookupResult.verified).
export type BrandColorConfidence = 'high' | 'medium' | 'low'

export interface BrandColorCandidate {
  primaryColor: string | null
  accentColor: string | null
  confidence: BrandColorConfidence
}

export interface BrandColorLookupResult {
  /**
   * True only when the top-ranked candidate is backed by a live, current public source with at
   * least medium confidence — never true for a plausible-looking guess or a low-confidence,
   * uncorroborated source. Downstream UI shows the "no public data" notice when this is false
   * (candidates is then always empty), rather than silently keeping stale/default values.
   */
  verified: boolean
  // Ranked best-first, highest confidence first; at most 3. Empty when verified is false.
  candidates: BrandColorCandidate[]
}

// ─── User ─────────────────────────────────────────────────────────────────────
export type UserRole = 'admin' | 'user'

export interface User {
  id: string         // same as email — used as CosmosDB item id
  email: string      // partition key
  name?: string      // display name — set when available (e.g. from Graph API)
  role: UserRole
  createdAt: string
  lastSeenAt?: string
}

import type { GccOutsourcingScenario, GccCombinableScenario, GccDashboardData, GccEligibilityRule, GccPayRateTable } from './gcc/types'
export * from './gcc/types'

// ─── Project ──────────────────────────────────────────────────────────────────
export type ProjectMode = 'outside-in' | 'inside-out' | 'gcc'

export interface ClientProfile {
  companyName: string
  industry?: string           // optional — Claude researches if blank
  geography?: string          // optional — Claude researches if blank
  headcountMin?: number       // optional — Claude researches if blank
  headcountMax?: number       // optional — Claude researches if blank
  targetHeadcount?: number    // optional — overrides AI-estimated headcount when provided
  functionScope: string[]
  ambitionLevel?: number      // 1 (conservative) → 5 (aggressive), defaults to 3
}

export interface Project {
  id: string
  name: string
  client: string
  industry: string
  mode: ProjectMode
  createdAt: string
  updatedAt: string
  branding: ClientBranding
  profile?: ClientProfile
  data?: DashboardData
  gccData?: GccDashboardData
  gccError?: string              // set when GCC generation fails; undefined while running or on success
  gccRoleCount?: number          // precomputed at save time — available even when gccData is stripped from list queries
  gccLabourSavingUSD?: number    // precomputed at save time
  mmsId?: string
  purpose?: string
  isDemo?: boolean                // true if loaded from demo datasets
  createdBy?: string              // email of the user who created this project
  sharedWith?: string[]           // emails of users this project has been shared with
  subfunctionCount?: number       // precomputed at save time — available even when data_gz is stripped from list queries
  automationBuildCount?: number   // precomputed at save time
  totalFteCount?: number          // precomputed current role FTE total for project cards
  /** Stage outputs from individual stage runs — persisted so the pipeline can resume without re-running completed stages.
   *  null = explicit clear (pipeline reset); omitted = keep stored value. */
  precomputed?: {
    stage1?: Stage1Result
    stage2?: Stage2Result
    stage3?: Stage3Result
    stage4?: Stage4Result
    stage5?: Stage5Result
    stage6?: Stage6Result
  } | null
  /** Set when this TN project was generated from a GCC retained-workforce analysis. */
  sourceGccProjectId?: string
  /** The outsourcing scenario that was used to determine retained headcount. */
  gccScenario?: GccOutsourcingScenario
  /** Set on a GCC-derived TN project when its analysis job fails; cleared by a later successful run (WI 2399). */
  gccTnError?: string
  /** When the GCC-derived TN analysis last failed. */
  gccTnFailedAt?: string
  /** ID of the latest analysis job started for this GCC-derived TN project, so a failed card can name it after a refresh (WI 2420). */
  gccTnJobId?: string
  /** IDs of TN projects derived from this GCC project via "Run TN Analysis". Stored on the GCC project. */
  derivedTnProjectIds?: string[]
  /** Set on a combined (roll-up) TN project assembled from two linked analyses — no AI run. */
  gccCombined?: GccCombinedSource
}

/** Where a combined TN dashboard came from, and when its sources were last changed. */
export interface GccCombinedSource {
  retainedProjectId: string
  offshoreProjectId: string
  offshoreScenario: GccCombinableScenario
  /** Constituent `updatedAt` values at build time — a mismatch marks the combined view out of date. */
  sourceUpdatedAt: { retained: string; offshore: string }
  builtAt: string
}

// ─── Settings ─────────────────────────────────────────────────────────────────
export type AIProvider = 'claude' | 'openai' | 'azure-openai'

export interface Settings {
  aiProvider: AIProvider
  anthropicApiKey: string
  openaiApiKey: string
  azureEndpoint: string
  azureApiKey: string
  azureDeployment: string
  azureApiVersion: string
}

// ─── IPC Payloads ─────────────────────────────────────────────────────────────
export interface GenerateOutsideInPayload {
  projectId: string
  profile: ClientProfile
  branding: ClientBranding
  provider: AIProvider
  apiKey: string
  sessionId?: string
  industryChoices?: string[]
}

export interface GenerateInsideOutPayload {
  projectId: string
  files: Array<{ base64: string; name: string }>
  clientName: string
  industry: string
  functionScope: string[]
  provider: AIProvider
  apiKey: string
  branding: ClientBranding
  mmsId?: string
  purpose?: string
  industryChoices?: string[]
  // When some stages have already been run individually, pass their results here
  // so runAllStages can skip them and pick up from the first incomplete stage.
  precomputed?: {
    stage1?: Stage1Result
    stage2?: Stage2Result
    stage3?: Stage3Result
    stage4?: Stage4Result
    stage5?: Stage5Result
  }
}

// Payload for running a single stage of the inside-out pipeline.
// Stages 1–2 need files (document text extraction); stages 3–6 only need
// the previous stage results passed via precomputed.
export interface RunInsideOutStagePayload {
  projectId: string
  stageIndex: 1 | 2 | 3 | 4 | 5 | 6
  files: Array<{ base64: string; name: string }>
  /** Extra documents the user attaches at intervention time. Parsed and extracted server-side before the stage runs. */
  supplementaryFiles?: Array<{ base64: string; name: string }>
  clientName: string
  industry: string
  functionScope: string[]
  provider: AIProvider
  apiKey: string
  precomputed?: {
    stage1?: Stage1Result
    stage2?: Stage2Result
    stage3?: Stage3Result
    stage4?: Stage4Result
    stage5?: Stage5Result
  }
}

export interface GenerateProgressEvent {
  subfunctionName: string
  index: number
  total: number
  status: 'generating' | 'done' | 'error'
  error?: string
}

export type OutsideInJobStatus = 'pending' | 'running' | 'succeeded' | 'failed'

export interface OutsideInJobState {
  id: string
  projectId: string
  status: OutsideInJobStatus
  createdAt: string
  updatedAt: string
  completedAt?: string
  error?: string
  progress: GenerateProgressEvent[]
}

export type InsideOutJobStatus = 'pending' | 'running' | 'succeeded' | 'failed'

export interface InsideOutJobState {
  id: string
  projectId: string
  status: InsideOutJobStatus
  createdAt: string
  updatedAt: string
  completedAt?: string
  error?: string
  progress: GenerateProgressEvent[]
}

export interface GenerateResult {
  success: boolean
  projectId: string
  error?: string
}

export type FeedbackJobStatus = 'pending' | 'running' | 'succeeded' | 'failed'

export interface FeedbackJobState {
  id: string
  projectId: string
  status: FeedbackJobStatus
  createdAt: string
  updatedAt: string
  completedAt?: string
  error?: string
  progress: FeedbackProgressEvent[]
}

export type RegenTab4JobStatus = 'pending' | 'running' | 'succeeded' | 'failed'

export interface RegenTab4JobState {
  id: string
  projectId: string
  status: RegenTab4JobStatus
  createdAt: string
  updatedAt: string
  completedAt?: string
  error?: string
  progress: FeedbackProgressEvent[]
}


export interface RegenTab4Payload {
  projectId: string
  provider: AIProvider
  apiKey: string
}

export interface ApplyFeedbackPayload {
  projectId: string
  feedback: string
  targetSubfunctionName: string | null  // null = add new subfunction, '' = apply to all, name = specific subfunction
  clientName: string
  industry: string
  provider: AIProvider
  apiKey: string
  brandColor?: string
}

export interface FeedbackProgressEvent {
  label: string
  status: 'generating' | 'done' | 'error'
  error?: string
}

// Named type for a subfunction/role entry produced by org structure extraction.
export interface SubfunctionEntry {
  name: string
  function?: string
  roles: Array<{ name: string; ftes: number }>
}

// ─── Stage Pipeline ──────────────────────────────────────────────────────────

export interface Stage1Result {
  subfunctionList: SubfunctionEntry[]
  dataGaps: string[]
  inferredIndustry?: string
}

export interface Stage2Result {
  subfunctions: Subfunction[]
  newWork: Array<{
    subfunctionName: string
    roleName: string
    newTasks: string[]
    buildSkills: string[]
  }>
}

export interface Stage3Result {
  subfunctions: Subfunction[]
}

export interface Stage4Result {
  process_areas: ProcessArea[]
}

export interface Stage5Result {
  automation_builds: AutomationBuild[]
}

export interface Stage6Result {
  journey_narrative: string[]
  closing_narrative: string[]
}

export type StagedResult =
  | Stage1Result
  | Stage2Result
  | Stage3Result
  | Stage4Result
  | Stage5Result
  | Stage6Result

export type StageStatus = 'idle' | 'running' | 'completed' | 'error'

export interface StageState {
  stageIndex: number
  status: StageStatus
  completedAt?: string
  error?: string
}

// ─── Dropdown Options ─────────────────────────────────────────────────────────
export interface PurposeOption {
  value: string
  label: string
  requiresMmsId: boolean
  defaultMmsId?: string
}

export interface DropdownOptions {
  industries: string[]
  purposeOptions: PurposeOption[]
}

export interface GenerateGccPayload {
  projectId: string
  clientName: string
  industry: string
  functionScope: string[]
  eligibilityRules: GccEligibilityRule[]
  payRateTable?: GccPayRateTable
  /** Automate-first threshold, whole % (WI 2048). Absent means the default (70). */
  automateFirstThresholdPct?: number
  /** Quick-win horizon, whole months (WI 2806). Absent means the default (6). */
  quickWinHorizonMonths?: number
  provider: AIProvider
  apiKey: string
  branding: ClientBranding
  uploadedFiles?: Array<{ base64: string; name: string }>
}

export interface CombineGccTnPayload {
  gccProjectId: string
  offshoreScenario: GccCombinableScenario
}

export interface RunTnOnRetainedPayload {
  gccProjectId: string
  scenario: GccOutsourcingScenario
  provider: AIProvider
  apiKey: string
  branding: ClientBranding
}

// ─── Demo Mode ───────────────────────────────────────────────────────────────
export interface DemoModeState {
  active: boolean
  source: 'cli' | 'settings' | 'env' | 'none'
}

export interface DemoDatasetInfo {
  slug: string
  companyName: string
  industry: string
  functionCount: number
  subfunctionCount: number
}

// ─── File classification ──────────────────────────────────────────────────────

/** Extensions treated as structured roster data. Single source of truth shared between
 *  the UI upload gate and the core pipeline router.
 *  .xls excluded — doc-parser always throws for legacy BIFF format.
 *  .json excluded — too broad; non-roster JSON files would be mislabelled as headcount data. */
export const STRUCTURED_FILE_EXTS = ['.xlsx', '.csv'] as const

/** Returns the lowercase file extension including the leading dot, or '' for extensionless names. */
export function getFileExt(name: string): string {
  const i = name.lastIndexOf('.')
  return i === -1 ? '' : name.slice(i).toLowerCase()
}
